import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, from, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  CsmUserProvisioningRequest,
  CsmUserProvisioningResponse,
  CsmUserStatusResponse
} from './model/csm-user-provisioning';
import { UserService } from '../../common/service/user.service';
import { CustomerService } from '../../common/service/customer.service';
import { Advisor } from '../home/advisor/model/advisor';

/**
 * Service to manage CSM (Customer Service Management) user provisioning
 * and status checks.
 */
@Injectable({
  providedIn: 'root'
})
export class CsmUserProvisioningService {
  private httpClient = inject(HttpClient);
  private userService = inject(UserService);
  private customerService = inject(CustomerService);

  /**
   * Resolves the external identifier formatted as `{customerNumber}-{contactNo}`.
   * Fetches the customer advisor details, looks up the contact number by email,
   * and falls back to contact number `1` if resolution fails.
   *
   * @param customerNumber The customer firm number
   * @returns Observable emitting the formatted externalId string and optional fetched Advisor
   */
  public getAdvisorExternalId(customerNumber: string): Observable<{ externalId: string; advisor?: Advisor }> {
    return from(this.customerService.getAdvisorDetail()).pipe(
      switchMap(advisor => {
        const email = advisor?.mailAddress??'';

        return this.userService.getAdvisorContact(email).pipe(
          switchMap(contact => {
            const contactNo = contact?.contactNo ?? '' ;
            return of({ externalId: `${customerNumber}-${contactNo}`, advisor });
          }),
          catchError(() => of({ externalId: `${customerNumber}-`, advisor }))
        );
      }),
      catchError(() => of({ externalId: `${customerNumber}-` }))
    );
  }

  /**
   * Retrieves the CSM provisioning status for a given externalId (KUNDENNR-ANSP_NR).
   * 
   * @param externalId Unique external identifier formatted as `{customerNumber}-{contactNo}`
   * @returns Observable emitting CSM provisioning status details
   */
  public getStatus(externalId: string): Observable<CsmUserStatusResponse> {
    return this.httpClient.get<CsmUserStatusResponse>(
      `${environment.APIBase}CsmUserProvisioning/status?externalId=${encodeURIComponent(externalId)}`
    );
  }

  /**
   * Triggers CSM user provisioning actions (e.g. LoginAdd).
   * 
   * @param request Payload containing provisioning actions and user metadata
   * @returns Observable emitting provisioning action result
   */
  public provisionUser(request: CsmUserProvisioningRequest): Observable<CsmUserProvisioningResponse> {
    return this.httpClient.post<CsmUserProvisioningResponse>(
      `${environment.APIBase}CsmUserProvisioning`,
      request
    );
  }
}

